/**
 * A sub's requests and notes on a job, in one list (Duncan 2026-10-24: "a way to look at the requests and notes ...
 * Instead of the E3 Project Manager, having to go through the line by line having to look for notes"): every request
 * and note on the lines of its tasks here, each with its task and line, newest first. Open is what's still open (not
 * resolved or delivered); All adds the answered ones and the notes. A request is answered here with the line's own
 * card and words (LineWork's RequestCard); "Open the line" opens the Work window at it.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { getSubRequests, answerWorkIssue } from '../../api';
import { ErrorBanner, SecondaryButton } from './SubUi';
import { RequestCard, NoteCard, PhotoViewer, newRequestId } from './LineWork';
import ScrollRail from './ScrollRail';

const isOpen = (x) => x.kind === 'request' && x.issue.status !== 'RESOLVED' && x.issue.status !== 'DELIVERED';
const muted = { fontSize: 12.5, color: 'var(--text-3)' };

/** The day an item came in, on this computer's calendar: '2026-10-24', and how the rail says it: "Oct 24". */
const pad = (n) => String(n).padStart(2, '0');
const dayKeyOf = (iso) => { const d = new Date(iso || 0); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const dayLabel = (key) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', y === new Date().getFullYear() ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' });
};
// The list scrolls inside this box, which fits the window under the project's pinned header and the card's tabs
// (2026-10-24: on a 13" laptop a fixed 560px ran past the bottom); the rail shows when the list runs past it.
const BOX_MAX = 'max(240px, min(560px, calc(100vh - 280px)))';

/** "Lone Star Middle School · TSK-0036 · Line 176, Employee Area". */
const whereOf = (x) => [x.task.location, x.task.code,
  `${x.line.no != null ? `Line ${x.line.no}` : 'A line added in Summit'}${x.line.area ? `, ${x.line.area}` : ''}${x.line.removed ? ' (removed)' : ''}`]
  .filter(Boolean).join(' · ');

export default function SubRequests({ projectId, subId, onOpenLine, onCount }) {
  const [data, setData] = useState(null);
  const [view, setView] = useState('open');
  const [busy, setBusy] = useState(null); // the item being answered
  const [error, setError] = useState(null);
  const [viewing, setViewing] = useState(null);
  // The scroll rail (2026-10-24, E3 Subs' "pencil line"): a dot per day, once the list runs past its box.
  const boxRef = useRef(null);
  const [rail, setRail] = useState({ on: false, items: [], narrow: false });
  const railKey = useRef(JSON.stringify(rail)); // set again only when the measure changes (PunchListTab's RailBox)

  const load = useCallback(() => getSubRequests(projectId, subId)
    .then((d) => {
      setData(d);
      if (onCount && d && d.on && !d.error) onCount(d.items.filter(isOpen).length);
    })
    .catch((e) => setData({ on: true, items: [], error: e.message || 'Couldn’t load the requests and notes' })), [projectId, subId, onCount]);
  useEffect(() => { load(); }, [load]);

  const answer = async (x, status, note, expectedAt) => {
    setBusy(x.key);
    setError(null);
    try {
      const r = await answerWorkIssue(x.lineId, x.issue.id,
        { status, ...(note ? { note } : {}), ...(expectedAt ? { expectedAt } : {}), requestId: newRequestId() });
      await load();
      return r || true;
    } catch (e) {
      setError(e.message || 'That didn’t go through');
      return null;
    } finally {
      setBusy(null);
    }
  };

  const open = data ? data.items.filter(isOpen) : [];
  const shown = !data ? [] : view === 'open' ? open : data.items;
  const shownKey = shown.map((x) => x.key).join('|');
  // Where each day starts in the box: measured after it lays out, and again as it changes size (a card opening for an
  // answer, photos loading). The rail is always there, as on the punch list's boxes (Duncan 2026-10-26).
  useLayoutEffect(() => {
    const box = boxRef.current;
    const setIfChanged = (next) => {
      const key = JSON.stringify(next);
      if (key === railKey.current) return;
      railKey.current = key;
      setRail(next);
    };
    if (!box) { setIfChanged({ on: false, items: [], narrow: false }); return undefined; }
    const measure = () => {
      const top0 = box.getBoundingClientRect().top - box.scrollTop;
      const firsts = [...box.querySelectorAll('[data-day-first]')];
      const items = firsts.map((el) => ({ id: el.dataset.day, label: dayLabel(el.dataset.day), count: Number(el.dataset.count), top: Math.round(el.getBoundingClientRect().top - top0) }));
      const on = items.length > 0;
      const narrow = box.parentElement ? box.parentElement.clientWidth < 560 : false;
      setIfChanged({ on, items, narrow });
    };
    measure();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => measure());
    if (ro) { ro.observe(box); if (box.firstElementChild) ro.observe(box.firstElementChild); }
    return () => { if (ro) ro.disconnect(); };
  }, [shownKey]);
  useEffect(() => { if (boxRef.current) boxRef.current.scrollTop = 0; }, [view]);

  if (!data) return <div style={{ ...muted, padding: '10px 0' }}>Loading the requests and notes…</div>;
  if (!data.on) return <div style={{ ...muted, padding: '10px 0' }}>Line work isn’t connected to E3 Subs yet.</div>;
  // Days, newest first: each day's first item carries its day and count for the rail.
  const dayCount = new Map();
  shown.forEach((x) => { const k = dayKeyOf(x.at); dayCount.set(k, (dayCount.get(k) || 0) + 1); });
  const firstOfDay = new Set();
  const seenDays = new Set();
  shown.forEach((x) => { const k = dayKeyOf(x.at); if (!seenDays.has(k)) { seenDays.add(k); firstOfDay.add(x.key); } });
  const chip = (k, label, n) => (
    <button key={k} type="button" onClick={() => setView(k)} aria-pressed={view === k}
      style={{
        padding: '3px 11px', borderRadius: 999, fontSize: 13, fontFamily: 'inherit', cursor: 'pointer',
        border: `1px solid ${view === k ? 'var(--border-strong)' : 'transparent'}`,
        background: view === k ? 'var(--bg-surface-2)' : 'transparent', color: view === k ? 'var(--text-1)' : 'var(--text-3)',
      }}>
      {label} <span className="mono" style={{ fontSize: 12 }}>{n}</span>
    </button>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', margin: '2px 0 10px' }}>
        {chip('open', 'Open', open.length)}
        {chip('all', 'All', data.items.length)}
      </div>
      {data.error && <div style={{ marginBottom: 8 }}><ErrorBanner>{data.error}</ErrorBanner></div>}
      {error && <div style={{ marginBottom: 8 }}><ErrorBanner>{error}</ErrorBanner></div>}
      {!shown.length && !data.error && (
        <div style={{ ...muted, padding: '6px 0' }}>{view === 'open' ? 'Nothing open: no request is waiting on E3.' : 'No requests or notes on this sub’s lines yet.'}</div>
      )}
      {shown.length > 0 && (
      <div style={{ display: 'flex', border: '1px solid var(--border-subtle)', borderRadius: 10, background: 'var(--bg-surface)', overflow: 'hidden' }}>
      {rail.on && <ScrollRail box={boxRef} items={rail.items} narrow={rail.narrow} title="Days" />}
      <div ref={boxRef} style={{ flex: 1, minWidth: 0, maxHeight: BOX_MAX, overflowY: 'auto', padding: '10px 12px 2px' }}>
      <div>
      {shown.map((x) => (
        <div key={x.key} style={{ marginBottom: 10 }}
          {...(firstOfDay.has(x.key) ? { 'data-day-first': '1', 'data-day': dayKeyOf(x.at), 'data-count': dayCount.get(dayKeyOf(x.at)) } : {})}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ ...muted, flex: '1 1 260px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={whereOf(x)}>{whereOf(x)}</span>
            <SecondaryButton onClick={() => onOpenLine(x.taskId, x.lineId)} style={{ height: 26, fontSize: 12.5, padding: '0 10px' }}>Open the line</SecondaryButton>
          </div>
          {x.kind === 'request'
            ? <RequestCard issue={x.issue} busy={busy === x.key ? 'answer' : busy ? 'other' : null} onAnswer={(status, note, expectedAt) => answer(x, status, note, expectedAt)}
              onOpenPhotos={(list, index) => setViewing({ list, index })} onExpired={load} />
            : <NoteCard note={x.note} />}
        </div>
      ))}
      </div>
      </div>
      </div>
      )}
      {viewing && <PhotoViewer list={viewing.list} index={viewing.index} onIndex={(i) => setViewing((v) => ({ ...v, index: i }))} onClose={() => setViewing(null)} />}
    </div>
  );
}
